/*
 * COPYRIGHT Motorola Solutions, INC.
 * ALL RIGHTS RESERVED.
 * MOTOROLA SOLUTIONS CONFIDENTIAL RESTRICTED
 */

import { DestroyRef, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { ServiceName } from '../../../assets/config/environment';
import { HealthService } from './health.service';
import { HeaderConfigService } from '@msi/commandcentral-common-header';
import { EnvironmentService } from './environment.service';
import { catchError, filter, map, switchMap, take, tap } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BehaviorSubject, firstValueFrom, Observable, of, timer } from 'rxjs';
import { CCAdminMeData } from '../model/CCAdminProxyModels';
@Injectable({
    providedIn: 'root'
})
export class CCAdminProxyService extends HealthService {
    private readonly STATUS_POLL_INTERVAL_MS = 10000;
    private isOnlineMode$: BehaviorSubject<boolean | null> = new BehaviorSubject(null);

    /**
     * Emits `true` once the app has detected an offline -> online transition
     * during this session. Latched: never resets back to false. Used by the
     * UI to prompt the user for a manual reload so the cloud common-header
     * (and dependent online-only services) can bootstrap cleanly.
     */
    public readonly requiresReload$ = new BehaviorSubject<boolean>(false);
    private previousOnlineMode: boolean | null = null;

    /**
     * E2E / debug override. When non-null, the status poller bypasses the real
     * `/status` request and emits this value instead. Use the
     * `setOnlineModeOverride` / `clearOnlineModeOverride` / `scheduleOnlineMode`
     * helpers (also exposed on `window.__offlineModeDebug`) to drive transitions
     * deterministically from tests or the dev console.
     */
    private onlineModeOverride: boolean | null = null;
    private scheduledTimeouts: ReturnType<typeof setTimeout>[] = [];

    constructor(
        protected http: HttpClient,
        protected env: EnvironmentService,
        private headerConfigService: HeaderConfigService,
        private destroyRef: DestroyRef
    ) {
        const serviceConfig = env.environment.IS_CLOUD_ENVIRONMENT ? [] : env.environment.SERVICES[ServiceName.CC_ADMIN_PROXY];
        const isEnabled = Boolean(serviceConfig.length) && !env.environment.IS_CLOUD_ENVIRONMENT;
        super(http, ServiceName.CC_ADMIN_PROXY, serviceConfig, '/actuator/health/readiness', isEnabled);
        if (!env.environment.IS_CLOUD_ENVIRONMENT) {
            this.initialized$.pipe(
                filter(value => !!value)
            ).subscribe(() => {
                this.overrideUrls();
                this.startStatusMonitoring();
            });
        }
    }

    /**
     * Returns the current online status synchronously.
     * `null` means the first check has not completed yet.
     */
    get currentStatus(): Observable<boolean | null> {
        return this.isOnlineMode$.asObservable();
    }

    /**
     * Waits for the first status check to complete after startup.
     * Resolves to `true` if the service is reachable, `false` otherwise.
     */
    waitForInitialStatus(): Promise<boolean> {
        return firstValueFrom(
            this.isOnlineMode$.pipe(
                filter((value): value is boolean => value !== null),
                take(1)
            )
        );
    }

    /**
     * Starts polling the /status endpoint immediately and then every 10 seconds.
     * Updates `isOnlineMode$` with `true` on success and `false` on error.
     */
    private startStatusMonitoring(): void {
        console.debug('[OfflineMode] Starting status monitoring (poll every %dms)', this.STATUS_POLL_INTERVAL_MS);
        timer(0, this.STATUS_POLL_INTERVAL_MS).pipe(
            switchMap((index) =>
                this.onlineModeOverride !== null
                    ? of(this.onlineModeOverride)
                    : this.fetchCurrentState().pipe(
                        catchError(() => of(false))
                    )
            ),
            takeUntilDestroyed(this.destroyRef)
        ).subscribe(isOnline => {
            if (this.previousOnlineMode !== isOnline) {
                console.debug('[OfflineMode] Status changed: %s → %s', this.previousOnlineMode, isOnline);
            }
            if (this.previousOnlineMode === false && isOnline === true) {
                console.debug('[OfflineMode] Offline→Online transition detected — requiresReload set');
                this.requiresReload$.next(true);
            }
            this.previousOnlineMode = isOnline;
            this.isOnlineMode$.next(isOnline);
        });
    }

    private fetchCurrentState() {
        return this.http.get(`${this.activeUrl$.value}/status`, {
            observe: 'body',
            responseType: 'json'
        }).pipe(
            map((res) => res['status'] === "ONLINE")
        );
    }

    private overrideUrls() {
        this.headerConfigService.updateRuntimeUrls({
            adminUrl: `${this.currentBaseUrl()}`
        });
    }

    /**
     * Fetches the full user profile, including the list of agencies the
     * current user can switch to. Only used by the agency-switcher modal —
     * the "current agency" value itself is sourced from `getCurrentAgency()`.
     */
    meEndpoint(): Observable<CCAdminMeData> {
        console.debug('[OfflineMode] Fetching /me endpoint');
        return this.http.get<CCAdminMeData>(`${this.currentBaseUrl()}/me?with-relations=all`, {
            observe: 'body',
            responseType: 'json'
        });
    }

    /**
     * Last known agency id from the backend. `null` until /current-agency has been called at least once.
     */
    public readonly currentAgencyId$ = new BehaviorSubject<string | null>(null);

    /**
     * Switches the active agency for the current user.
     * On success, refreshes /current-agency so `currentAgencyId$` is updated.
     */
    switchAgency(agencyId: string): Observable<string> {
        console.debug('[OfflineMode] Switching agency to: %s', agencyId);
        return this.http.patch(`${this.currentBaseUrl()}/current-agency`, null, {
            params: new HttpParams().set('agency', agencyId),
            observe: 'body',
            responseType: 'text'
        }).pipe(
            tap(() => console.debug('[OfflineMode] Agency switch PATCH succeeded, refreshing /current-agency')),
            switchMap(() => this.getCurrentAgency().pipe(map(() => agencyId)))
        );
    }

    getCurrentAgency(): Observable<string> {
        console.debug('[OfflineMode] Fetching /current-agency endpoint');
        return this.http.get(`${this.currentBaseUrl()}/current-agency`, {
            observe: 'body',
            responseType: 'text'
        }).pipe(
            tap((agencyId) => {
                console.debug('[OfflineMode] /current-agency response: %s', agencyId);
                this.currentAgencyId$.next(agencyId?.length ? agencyId : null);
            })
        );
    }

    currentBaseUrl(): string {
        return `${this.activeUrl$.value}/ui-proxy`;
    }

    // ---------------------------------------------------------------------
    // E2E / debug helpers
    // ---------------------------------------------------------------------

    /**
     * Force the reported online mode to a fixed value and immediately push it
     * to subscribers. Real `/status` polling is bypassed until cleared.
     *
     * @example  ccAdminProxyService.setOnlineModeOverride(false); // go offline now
     */
    setOnlineModeOverride(isOnline: boolean): void {
        console.debug('[OfflineMode] Debug override set: %s', isOnline);
        this.onlineModeOverride = isOnline;
        if (this.previousOnlineMode === false && isOnline === true) {
            this.requiresReload$.next(true);
        }
        this.previousOnlineMode = isOnline;
        this.isOnlineMode$.next(isOnline);
    }

    /**
     * Remove a previously-set override; the next poll tick will use the real
     * `/status` endpoint again.
     */
    clearOnlineModeOverride(): void {
        this.onlineModeOverride = null;
    }

    /** Whether an override is currently active (used by the Debug Menu UI). */
    get hasOnlineModeOverride(): boolean {
        return this.onlineModeOverride !== null;
    }

    /** Number of pending scheduled mode changes (used by the Debug Menu UI). */
    get pendingScheduledChanges(): number {
        return this.scheduledTimeouts.length;
    }
}
